// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Button, Table, Icon, Message, Loader, Pagination, Dropdown } from 'semantic-ui-react';
import { brokerageApi, Brokerage } from '../api/brokerageApi';

function BrokerageListPage() {
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const navigate = useNavigate();

  const loadBrokerages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await brokerageApi.getAll({
        page: currentPage,
        size: pageSize
      });
      setBrokerages(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load brokerages');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadBrokerages();
  }, [loadBrokerages]);

  const handleDelete = async (id: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this brokerage?')) {
      return;
    }

    try {
      await brokerageApi.delete(id);
      await loadBrokerages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete brokerage');
    }
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='building' />
        <Header.Content>
          Brokerage Management
          <Header.Subheader>Manage brokerages in the system</Header.Subheader>
        </Header.Content>
      </Header>

      <div style={{ marginBottom: '20px' }}>
        <Button primary onClick={() => navigate('/brokerages/new')}>
          <Icon name='plus' />
          New Brokerage
        </Button>
      </div>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>Loading brokerages...</Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Registration Number</Table.HeaderCell>
                <Table.HeaderCell>Contact Email</Table.HeaderCell>
                <Table.HeaderCell>City, State</Table.HeaderCell>
                <Table.HeaderCell>CRM Solution</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {brokerages.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7} textAlign='center'>
                    No brokerages found. Click "New Brokerage" to create one.
                  </Table.Cell>
                </Table.Row>
              ) : (
                brokerages.map((brokerage) => (
                  <Table.Row key={brokerage.id}>
                    <Table.Cell>{brokerage.name}</Table.Cell>
                    <Table.Cell>{brokerage.registrationNumber || '-'}</Table.Cell>
                    <Table.Cell>{brokerage.contactEmail || '-'}</Table.Cell>
                    <Table.Cell>
                      {brokerage.townCity && brokerage.stateProvinceCounty
                        ? `${brokerage.townCity}, ${brokerage.stateProvinceCounty}`
                        : brokerage.townCity || brokerage.stateProvinceCounty || '-'}
                    </Table.Cell>
                    <Table.Cell>{brokerage.crmSolutionName || '-'}</Table.Cell>
                    <Table.Cell>
                      <Icon
                        name={brokerage.status === 'ACTIVE' ? 'check circle' : 'ban'}
                        color={brokerage.status === 'ACTIVE' ? 'green' : 'red'}
                      />
                      {brokerage.status || 'ACTIVE'}
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/brokerages/${brokerage.id}/edit`)}
                          title='Edit Brokerage'
                        />
                        <Button
                          icon='paint brush'
                          color='purple'
                          onClick={() => navigate(`/branding/${brokerage.id}/edit`)}
                          title='Configure Branding'
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDelete(brokerage.id!)}
                          title='Delete Brokerage'
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#666' }}>
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} brokerage{totalElements === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>Items per page:</span>
              <Dropdown
                compact
                selection
                value={pageSize}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 }
                ]}
                onChange={(_e, data) => {
                  setPageSize(data.value as number);
                  setCurrentPage(0); // Reset to first page when changing page size
                }}
              />
              <Pagination
                activePage={currentPage + 1}
                totalPages={totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                siblingRange={1}
                boundaryRange={0}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                nextItem={{ content: <Icon name='chevron right' />, icon: true }}
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
}

export default BrokerageListPage;
